import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { uniq } from "lodash";
import { modal } from "Components/General/Modal/AntdModal";
import { contactsStore } from "Stores/ContactsStore";
import { userInfoStore } from "Stores";
import { useEmailAttachments } from "utils/hooks/useEmailAttachments";
import { extractIdFromUId } from "Pages/Project/Communication/utils/extractIdFromUId";
import { EmailSubmitStore, MailInitialRecipients } from "Pages/Project/Communication/Stores/EmailSubmitStore";
import { MailType } from "Pages/Project/Communication/EmailSendModal/EmailSendModal";
import { I18N_NS } from "Config/i18n";

export type EmailInvalidData = Pick<API_MailResponse, "to" | "cc" | "bcc" | "attachments" | "from" | "importance">;

export interface EmailValidData extends API_MailResponse, MailInitialRecipients {}

type UseCheckDeletedFilesAndUsersReturn = {
  checkDeletedFilesAndUsers: (
    invalidData: EmailInvalidData & MailInitialRecipients,
    type: MailType
  ) => Promise<EmailValidData>;
};

export const useCheckDeletedFilesAndUsers = (
  emailSubmitStore: EmailSubmitStore
): UseCheckDeletedFilesAndUsersReturn => {
  const { t } = useTranslation(I18N_NS.PROJECT);
  const { mapAttachments, checkAttachmentsAccess } = useEmailAttachments(emailSubmitStore);

  const filterCurrentUser = (recipients: string[]) => {
    const currentUserUid = `@user${userInfoStore.id}`;
    return recipients.filter((item) => item !== currentUserUid);
  };

  const checkDeletedFilesAndUsers = useCallback(
    (invalidData: EmailInvalidData & MailInitialRecipients, type: MailType): Promise<EmailValidData> => {
      return new Promise((resolve) => {
        let fileDeleted = false;
        let userDeleted = false;
        const messages: string[] = [];
        const activeProjectUsersIds = new Map(contactsStore.projectUsers.map((user) => [user.id, user.active]));
        const needCheckAttachments = type === "forward" || type === "attach" || type === "draft";
        const newFormData: EmailInvalidData = { ...invalidData };
        let invalidDataTo = [...invalidData.to]; // Default value for "forward" action

        const checkValidUsers = (data: string[]) =>
          data.filter((user) => {
            if (user?.startsWith("@user")) {
              const userId = +extractIdFromUId(user).value;
              const isActive = activeProjectUsersIds.get(userId) === true;
              if (!isActive) {
                userDeleted = true;
              }
              return isActive;
            }
            return true;
          });

        const checkValidAttachments = (data: API_FileAttachment[] = []) => {
          return data?.filter((item) => {
            if (item?.deleted) {
              fileDeleted = true;
              return false;
            }
            return true;
          });
        };

        if (type === "reply_all") {
          invalidDataTo = [...filterCurrentUser([...invalidDataTo, invalidData.from])];
        }
        if (type === "reply") {
          invalidDataTo = [invalidData.from];
        }
        if (type === "forward" || type === "attach") {
          newFormData.to = [];
          newFormData.cc = [];
          newFormData.bcc = [];
        } else {
          newFormData.to = uniq(checkValidUsers(invalidDataTo));
          newFormData.cc = uniq(checkValidUsers(invalidData.cc));
          newFormData.bcc = uniq(checkValidUsers(invalidData.bcc));
        }

        const validAttachments = checkValidAttachments(invalidData.attachments);

        if (needCheckAttachments && validAttachments.length > 0) {
          mapAttachments(validAttachments);
          checkAttachmentsAccess(validAttachments);
        }

        newFormData.attachments = validAttachments;

        const onResolve = () => resolve(newFormData as EmailValidData);

        if (fileDeleted) {
          messages.push(t("Files that are no longer available were removed."));
        }
        if (userDeleted) {
          messages.push(t("Recipients that are no longer available were removed."));
        }
        if (messages.length) {
          modal.warning({
            title: t("Changes were made"),
            zIndex: 1011,
            content: messages.map((item) => <div key={item}>{item}</div>),
            okText: t("Confirm"),
            onOk: onResolve,
          });
        } else {
          onResolve();
        }
      });
    },
    [mapAttachments, checkAttachmentsAccess]
  );

  return { checkDeletedFilesAndUsers };
};

